import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import { legalEntityQueryConfig } from '@webapp/platform/legal-entities/query-configs/legalEntityQueryConfig'

interface UseQueryLegalEntityReturn {
  entity: Nullable<LegalEntity>
  isLoading: boolean
  isError: boolean
}

/**
 * Selects a single LegalEntity by `uuid` from the entity list.
 *
 * The list endpoint returns every entity in one request with all the fields
 * the detail page needs, so rather than assuming a single-resource GET endpoint
 * we read the list — the same approach as `useQueryLegalEntityUniqueFields`.
 * Reusing the shared `list` query config means React Query dedupes: the list
 * view, the breadcrumb and this hook reuse a single cached request.
 */
export const useQueryLegalEntity = (
  uuid: string
): UseQueryLegalEntityReturn => {
  const { data, isLoading, isError } = useQuery(legalEntityQueryConfig.list())

  const entity = useMemo(
    () =>
      (data ?? [])
        .map((item) => new LegalEntity(item))
        .find((item) => item.uuid === uuid) ?? null,
    [data, uuid]
  )

  return {
    entity,
    isLoading,
    isError,
  }
}
